import { useState, useEffect, useCallback } from 'react';
import { Card, Btn, Input, Select, Field } from '../../components/UI';
import NrpLookup from '../../components/NrpLookup';
import { useToast } from '../../components/Toast';
import { binkarAPI, personelAPI } from '../../utils/api';
import ApprovalHistoryModal from '../../components/ApprovalHistoryModal';
import { exportCSV, printTable } from '../../utils/exportHelper';

const StatusBadge = ({ status }) => {
  const colors = {
    MENUNGGU: { bg: '#FEF3C7', color: '#92400E' },
    DIPROSES: { bg: '#DBEAFE', color: '#1E40AF' },
    SELESAI: { bg: '#DCFCE7', color: '#166534' },
    GAGAL: { bg: '#FEE2E2', color: '#991B1B' },
    DITOLAK: { bg: '#FEE2E2', color: '#991B1B' },
    DRAFT: { bg: '#F3F4F6', color: '#374151' },
    FINAL: { bg: '#E0E7FF', color: '#3730A3' },
    AKTIF: { bg: '#ECFDF5', color: '#047857' },
    BATAL: { bg: '#FEE2E2', color: '#991B1B' },
  };
  const c = colors[status] || { bg: '#F3F4F6', color: '#374151' };
  return <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, ...c }}>{status}</span>;
};

const th = { padding: '10px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748B', borderBottom: '2px solid #E2E8F0' };
const td = { padding: '10px 14px', fontSize: 13, borderBottom: '1px solid #F1F5F9' };
const rowStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 16 };

const Pager = ({ page, total, limit, onChange }) => {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  if (totalPages <= 1) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, justifyContent: 'center' }}>
      <Btn onClick={() => onChange(page - 1)} disabled={page <= 1} variant="secondary">← Prev</Btn>
      <span style={{ fontSize: 12, color: '#6B7280', padding: '0 8px' }}>Hal {page} / {totalPages}</span>
      <Btn onClick={() => onChange(page + 1)} disabled={page >= totalPages} variant="secondary">Next →</Btn>
    </div>
  );
};

// ── UPLOAD KP ──────────────────────────────────────────
const UploadKPTab = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ status_kp: '', pangkat_baru: '', file_name: '', jumlah_data: '' });
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await binkarAPI.getKPUpload(`?page=${page}&limit=10`);
      if (r.success) { setData(r.data); setTotal(r.pagination?.total || 0); }
    } catch (err) { toast(err.message, 'error'); }
    finally { setLoading(false); }
  }, [page, toast]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.status_kp || !form.file_name) { toast('Status KP dan nama file harus diisi', 'error'); return; }
    try {
      const r = await binkarAPI.createKPUpload(form);
      if (r.success) { toast('Upload KP berhasil ditambahkan'); setForm({ status_kp: '', pangkat_baru: '', file_name: '', jumlah_data: '' }); load(); }
    } catch (err) { toast(err.message, 'error'); }
  };

  return (
    <>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Upload Kepangkatan (KP)</h3>
        <div style={rowStyle}>
          <Field label="Status KP"><Select value={form.status_kp} onChange={(e) => setForm({ ...form, status_kp: e.target.value })}
            options={[{ value: '', label: '-- Pilih --' }, { value: 'reguler_dalam', label: 'Reguler Dalam' }, { value: 'reguler_luar', label: 'Reguler Luar' }, { value: 'penghargaan', label: 'Penghargaan' }, { value: 'pindah_golongan', label: 'Pindah Golongan' }]} /></Field>
          <Field label="Pangkat Baru"><Input value={form.pangkat_baru} onChange={(e) => setForm({ ...form, pangkat_baru: e.target.value })} placeholder="Pangkat baru" /></Field>
        </div>
        <div style={rowStyle}>
          <Field label="Nama File"><Input value={form.file_name} onChange={(e) => setForm({ ...form, file_name: e.target.value })} placeholder="kp-upload.xlsx" /></Field>
          <Field label="Jumlah Data"><Input type="number" value={form.jumlah_data} onChange={(e) => setForm({ ...form, jumlah_data: e.target.value })} placeholder="0" /></Field>
        </div>
        <Btn onClick={handleSubmit} disabled={loading}>{loading ? 'Mengunggah...' : 'Upload'}</Btn>
      </Card>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Daftar Upload KP</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#F8FAFC' }}>
              <th style={th}>Status KP</th><th style={th}>Pangkat Baru</th><th style={th}>File</th><th style={th}>Jumlah Data</th><th style={th}>Status</th><th style={th}>Waktu Upload</th>
            </tr></thead>
            <tbody>
              {data.map(u => (
                <tr key={u.id}>
                  <td style={td}>{u.status_kp}</td>
                  <td style={td}>{u.pangkat_baru || '-'}</td>
                  <td style={td}>{u.file_name}</td>
                  <td style={td}>{u.jumlah_data} baris</td>
                  <td style={td}><StatusBadge status={u.status} /></td>
                  <td style={{ ...td, fontSize: 12, color: '#64748B' }}>{new Date(u.uploaded_at).toLocaleString('id')}</td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: '#9CA3AF' }}>Belum ada data upload</td></tr>}
            </tbody>
          </table>
        </div>
        <Pager page={page} total={total} limit={10} onChange={setPage} />
      </Card>
    </>
  );
};

// ── NOMINATIF KP ───────────────────────────────────────
const NominatifKPTab = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [historyModal, setHistoryModal] = useState(null);
  const [form, setForm] = useState({ nrp: '', status_kp: '', pangkat_baru: '', tmt_pangkat_baru: '' });
  const [selPers, setSelPers] = useState(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await binkarAPI.getNominatif(`?page=${page}&limit=10`);
      if (r.success) { setData(r.data); setTotal(r.pagination?.total || 0); }
    } catch (err) { toast(err.message, 'error'); }
    finally { setLoading(false); }
  }, [page, toast]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.nrp || !form.status_kp) { toast('NRP dan Status KP harus diisi', 'error'); return; }
    try {
      const r = await binkarAPI.createNominatif({ ...form, pangkat_lama: selPers?.pangkat || '' });
      if (r.success) { toast('Nominatif KP berhasil disimpan'); setForm({ nrp: '', status_kp: '', pangkat_baru: '', tmt_pangkat_baru: '' }); setSelPers(null); load(); }
    } catch (err) { toast(err.message, 'error'); }
  };

  return (
    <>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Tambah Nominatif KP</h3>
        <div style={rowStyle}>
          <Field label="NRP Personel" required><NrpLookup value={selPers} onSelect={(p) => { setSelPers(p); setForm(f=>({...f, nrp: p.nrp})); }} onClear={() => { setSelPers(null); setForm(f=>({...f, nrp: ''})); }} /></Field>
          <Field label="Status KP"><Select value={form.status_kp} onChange={(e) => setForm({ ...form, status_kp: e.target.value })}
            options={[{ value: '', label: '-- Pilih --' }, { value: 'reguler_dalam', label: 'Reguler Dalam' }, { value: 'reguler_luar', label: 'Reguler Luar' }]} /></Field>
        </div>
        <div style={rowStyle}>
          <Field label="Pangkat Baru"><Input value={form.pangkat_baru} onChange={(e) => setForm({ ...form, pangkat_baru: e.target.value })} placeholder="Pangkat baru" /></Field>
          <Field label="TMT Pangkat Baru"><Input type="date" value={form.tmt_pangkat_baru} onChange={(e) => setForm({ ...form, tmt_pangkat_baru: e.target.value })} /></Field>
        </div>
        <Btn onClick={handleSubmit} disabled={loading}>{loading ? 'Menyimpan...' : 'Simpan'}</Btn>
      </Card>
      <Card>
        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, margin:0 }}>Daftar Nominatif KP ({total})</h3>
          <div style={{ display:'flex',gap:6 }}>
            <Btn size="sm" variant="ghost" onClick={() => { const h=['NRP','Nama','Pangkat','Status KP','Pangkat Baru','TMT']; exportCSV('nominatif_kp', h, data.map(r=>[r.nrp,r.nama,r.pangkat,r.status_kp,r.pangkat_baru,r.tmt_pangkat_baru])); }}>CSV</Btn>
            <Btn size="sm" variant="ghost" onClick={() => { const h=['NRP','Nama','Pangkat','Status KP','Pangkat Baru','TMT']; printTable('Nominatif KP', h, data.map(r=>[r.nrp,r.nama,r.pangkat,r.status_kp,r.pangkat_baru,r.tmt_pangkat_baru])); }}>Print</Btn>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#F8FAFC' }}>
              <th style={th}>NRP</th><th style={th}>Nama</th><th style={th}>Pangkat</th><th style={th}>Status KP</th><th style={th}>Pangkat Baru</th><th style={th}>TMT</th><th style={th}>Aksi</th>
            </tr></thead>
            <tbody>
              {data.map(r => (
                <tr key={r.id}>
                  <td style={{ ...td, fontFamily: 'monospace' }}>{r.nrp}</td>
                  <td style={td}>{r.nama}</td>
                  <td style={td}>{r.pangkat}</td>
                  <td style={td}>{r.status_kp}</td>
                  <td style={td}>{r.pangkat_baru || '-'}</td>
                  <td style={{ ...td, fontSize: 12 }}>{r.tmt_pangkat_baru || '-'}</td>
                  <td style={td}>
                    <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                      <Btn size="sm" onClick={async () => {
                        if (!window.confirm(`Setujui KP ${r.nrp} → ${r.pangkat_baru}? Data master personel akan diperbarui.`)) return;
                        try { await binkarAPI.approveKP(r.id); toast(`KP ${r.nrp} disetujui`); load(); } catch (e) { toast(e.message, 'error'); }
                      }}>Approve</Btn>
                      <Btn size="sm" variant="warning" onClick={async () => {
                        const catatan = window.prompt('Alasan penolakan:');
                        if (catatan === null) return;
                        try { await binkarAPI.rejectKP(r.id, catatan); toast(`KP ${r.nrp} ditolak`); load(); } catch (e) { toast(e.message, 'error'); }
                      }}>Tolak</Btn>
                      <Btn size="sm" variant="danger" onClick={async () => {
                        if (!window.confirm(`Hapus nominatif ${r.nrp}?`)) return;
                        try { await binkarAPI.deleteNominatif(r.id); toast('Nominatif dihapus'); load(); } catch (e) { toast(e.message, 'error'); }
                      }}>Hapus</Btn>
                      <Btn size="sm" variant="secondary" onClick={() => setHistoryModal(r.id)}>Riwayat</Btn>
                    </div>
                  </td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#9CA3AF' }}>Belum ada nominatif</td></tr>}
            </tbody>
          </table>
        </div>
        <Pager page={page} total={total} limit={10} onChange={setPage} />
      </Card>
      {historyModal && <ApprovalHistoryModal module="binkar" referenceId={historyModal} onClose={() => setHistoryModal(null)} />}
    </>
  );
};

// ── PEMINDAHAN ─────────────────────────────────────────
const PemindahanTab = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ nrp: '', kotama_id_baru: '', tmt_jabatan: '', periode: '' });
  const [selPers, setSelPers] = useState(null);
  const [kotamaList, setKotamaList] = useState([]);
  const [historyModal, setHistoryModal] = useState(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await binkarAPI.getPemindahan(`?page=${page}&limit=10`);
      if (r.success) { setData(r.data); setTotal(r.pagination?.total || 0); }
    } catch (err) { toast(err.message, 'error'); }
    finally { setLoading(false); }
  }, [page, toast]);

  useEffect(() => { load(); personelAPI.getKotama().then(r => setKotamaList(r.data || [])).catch(() => {}); }, [load]);

  const handleSubmit = async () => {
    if (!form.nrp || !form.kotama_id_baru) { toast('NRP dan Kotama Tujuan harus diisi', 'error'); return; }
    try {
      const r = await binkarAPI.createPemindahan(form);
      if (r.success) { toast('Pemindahan berhasil disimpan'); setForm({ nrp: '', kotama_id_baru: '', tmt_jabatan: '', periode: '' }); setSelPers(null); load(); }
    } catch (err) { toast(err.message, 'error'); }
  };

  return (
    <>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Tambah Pemindahan (Transfer)</h3>
        <div style={rowStyle}>
          <Field label="NRP Personel" required><NrpLookup value={selPers} onSelect={(p) => { setSelPers(p); setForm(f=>({...f, nrp: p.nrp})); }} onClear={() => { setSelPers(null); setForm(f=>({...f, nrp: ''})); }} /></Field>
          <Field label="Kotama Tujuan" required><Select value={form.kotama_id_baru} onChange={(e) => setForm({ ...form, kotama_id_baru: e.target.value })}
            options={[{ value: '', label: '-- Pilih Kotama Tujuan --' }, ...kotamaList.map(k => ({ value: k.id, label: k.nama }))]} /></Field>
        </div>
        <div style={rowStyle}>
          <Field label="TMT Jabatan"><Input type="date" value={form.tmt_jabatan} onChange={(e) => setForm({ ...form, tmt_jabatan: e.target.value })} /></Field>
          <Field label="Periode"><Input value={form.periode} onChange={(e) => setForm({ ...form, periode: e.target.value })} placeholder="Contoh: 2026-I" /></Field>
        </div>
        {selPers && <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 12 }}>Kotama saat ini: <strong>{selPers.kotama || '-'}</strong></div>}
        <Btn onClick={handleSubmit} disabled={loading}>{loading ? 'Menyimpan...' : 'Simpan'}</Btn>
      </Card>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Daftar Pemindahan ({total})</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#F8FAFC' }}>
              <th style={th}>NRP</th><th style={th}>Nama</th><th style={th}>Kotama Lama</th><th style={th}>Kotama Baru</th><th style={th}>TMT Jabatan</th><th style={th}>Status</th><th style={th}>Aksi</th>
            </tr></thead>
            <tbody>
              {data.map(r => (
                <tr key={r.id}>
                  <td style={{ ...td, fontFamily: 'monospace' }}>{r.nrp}</td>
                  <td style={td}>{r.nama}</td>
                  <td style={td}>{r.kotama_lama || '-'}</td>
                  <td style={td}>{r.kotama_baru || '-'}</td>
                  <td style={{ ...td, fontSize: 12 }}>{r.tmt_jabatan || '-'}</td>
                  <td style={td}><StatusBadge status={r.status} /></td>
                  <td style={td}>
                    <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                      {r.status !== 'SELESAI' && r.status !== 'DITOLAK' && <>
                        <Btn size="sm" onClick={async () => {
                          if (!window.confirm(`Setujui pemindahan ${r.nrp}? Kotama personel akan diperbarui.`)) return;
                          try { await binkarAPI.approvePemindahan(r.id); toast(`Pemindahan ${r.nrp} disetujui`); load(); } catch (e) { toast(e.message, 'error'); }
                        }}>Approve</Btn>
                        <Btn size="sm" variant="warning" onClick={async () => {
                          const catatan = window.prompt('Alasan penolakan:');
                          if (catatan === null) return;
                          try { await binkarAPI.rejectPemindahan(r.id, catatan); toast(`Pemindahan ${r.nrp} ditolak`); load(); } catch (e) { toast(e.message, 'error'); }
                        }}>Tolak</Btn>
                      </>}
                      {r.status !== 'SELESAI' && <Btn size="sm" variant="danger" onClick={async () => {
                        if (!window.confirm(`Hapus pemindahan ${r.nrp}?`)) return;
                        try { await binkarAPI.deletePemindahan(r.id); toast('Pemindahan dihapus'); load(); } catch (e) { toast(e.message, 'error'); }
                      }}>Hapus</Btn>}
                      <Btn size="sm" variant="secondary" onClick={() => setHistoryModal(r.id)}>Riwayat</Btn>
                    </div>
                  </td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#9CA3AF' }}>Belum ada pemindahan</td></tr>}
            </tbody>
          </table>
        </div>
        <Pager page={page} total={total} limit={10} onChange={setPage} />
      </Card>
      {historyModal && <ApprovalHistoryModal module="binkar" referenceId={historyModal} onClose={() => setHistoryModal(null)} />}
    </>
  );
};

// ── PENEMPATAN ─────────────────────────────────────────
const PenempatanTab = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ nrp: '', satuan_baru: '', tmt_jabatan: '', gelombang: '' });
  const [selPers, setSelPers] = useState(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await binkarAPI.getPenempatan(`?page=${page}&limit=10`);
      if (r.success) { setData(r.data); setTotal(r.pagination?.total || 0); }
    } catch (err) { toast(err.message, 'error'); }
    finally { setLoading(false); }
  }, [page, toast]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.nrp) { toast('NRP harus diisi', 'error'); return; }
    try {
      const r = await binkarAPI.createPenempatan(form);
      if (r.success) { toast('Penempatan berhasil disimpan'); setForm({ nrp: '', satuan_baru: '', tmt_jabatan: '', gelombang: '' }); setSelPers(null); load(); }
    } catch (err) { toast(err.message, 'error'); }
  };

  return (
    <>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Tambah Penempatan (Placement)</h3>
        <div style={rowStyle}>
          <Field label="NRP Personel" required><NrpLookup value={selPers} onSelect={(p) => { setSelPers(p); setForm(f=>({...f, nrp: p.nrp})); }} onClear={() => { setSelPers(null); setForm(f=>({...f, nrp: ''})); }} /></Field>
          <Field label="Satuan Baru"><Input value={form.satuan_baru} onChange={(e) => setForm({ ...form, satuan_baru: e.target.value })} placeholder="Satuan penempatan" /></Field>
        </div>
        <div style={rowStyle}>
          <Field label="TMT Jabatan"><Input type="date" value={form.tmt_jabatan} onChange={(e) => setForm({ ...form, tmt_jabatan: e.target.value })} /></Field>
          <Field label="Gelombang"><Input value={form.gelombang} onChange={(e) => setForm({ ...form, gelombang: e.target.value })} placeholder="Contoh: A, B, C" /></Field>
        </div>
        <Btn onClick={handleSubmit} disabled={loading}>{loading ? 'Menyimpan...' : 'Simpan'}</Btn>
      </Card>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Daftar Penempatan ({total})</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#F8FAFC' }}>
              <th style={th}>NRP</th><th style={th}>Satuan Lama</th><th style={th}>Satuan Baru</th><th style={th}>TMT Jabatan</th><th style={th}>Gelombang</th><th style={th}>Status</th><th style={th}>Aksi</th>
            </tr></thead>
            <tbody>
              {data.map(r => (
                <tr key={r.id}>
                  <td style={td}>{r.nrp}</td>
                  <td style={td}>{r.satuan_lama || '-'}</td>
                  <td style={td}>{r.satuan_baru}</td>
                  <td style={{ ...td, fontSize: 12 }}>{r.tmt_jabatan || '-'}</td>
                  <td style={td}>{r.gelombang || '-'}</td>
                  <td style={td}><StatusBadge status={r.status} /></td>
                  <td style={td}>
                    {r.status !== 'SELESAI' && <Btn size="sm" variant="danger" onClick={async () => {
                      if (!window.confirm(`Hapus penempatan ${r.nrp}?`)) return;
                      try { await binkarAPI.deletePenempatan(r.id); toast('Penempatan dihapus'); load(); } catch (e) { toast(e.message, 'error'); }
                    }}>Hapus</Btn>}
                  </td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: '#9CA3AF' }}>Belum ada penempatan</td></tr>}
            </tbody>
          </table>
        </div>
        <Pager page={page} total={total} limit={10} onChange={setPage} />
      </Card>
    </>
  );
};

// ── GAJI PENETAPAN ────────────────────────────────────
const GajiTab = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ nrp: '', pangkat_baru: '', gaji_baru: '', tmt_gaji: '' });
  const [selPers, setSelPers] = useState(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await binkarAPI.getGaji(`?page=${page}&limit=10`);
      if (r.success) { setData(r.data); setTotal(r.pagination?.total || 0); }
    } catch (err) { toast(err.message, 'error'); }
    finally { setLoading(false); }
  }, [page, toast]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.nrp) { toast('NRP harus diisi', 'error'); return; }
    try {
      const r = await binkarAPI.createGaji(form);
      if (r.success) { toast('Penetapan gaji berhasil disimpan'); setForm({ nrp: '', pangkat_baru: '', gaji_baru: '', tmt_gaji: '' }); setSelPers(null); load(); }
    } catch (err) { toast(err.message, 'error'); }
  };

  return (
    <>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Tambah Penetapan Gaji</h3>
        <div style={rowStyle}>
          <Field label="NRP Personel" required><NrpLookup value={selPers} onSelect={(p) => { setSelPers(p); setForm(f=>({...f, nrp: p.nrp})); }} onClear={() => { setSelPers(null); setForm(f=>({...f, nrp: ''})); }} /></Field>
          <Field label="Pangkat Baru"><Input value={form.pangkat_baru} onChange={(e) => setForm({ ...form, pangkat_baru: e.target.value })} placeholder="Pangkat baru" /></Field>
        </div>
        <div style={rowStyle}>
          <Field label="Gaji Baru"><Input type="number" value={form.gaji_baru} onChange={(e) => setForm({ ...form, gaji_baru: e.target.value })} placeholder="Gaji baru" /></Field>
          <Field label="TMT Gaji"><Input type="date" value={form.tmt_gaji} onChange={(e) => setForm({ ...form, tmt_gaji: e.target.value })} /></Field>
        </div>
        <Btn onClick={handleSubmit} disabled={loading}>{loading ? 'Menyimpan...' : 'Simpan'}</Btn>
      </Card>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Daftar Penetapan Gaji ({total})</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#F8FAFC' }}>
              <th style={th}>NRP</th><th style={th}>Nama</th><th style={th}>Pangkat Lama</th><th style={th}>Pangkat Baru</th><th style={{ ...th, textAlign: 'right' }}>Gaji Lama</th><th style={{ ...th, textAlign: 'right' }}>Gaji Baru</th><th style={th}>TMT</th><th style={th}>Status</th><th style={th}>Aksi</th>
            </tr></thead>
            <tbody>
              {data.map(r => (
                <tr key={r.id}>
                  <td style={{ ...td, fontFamily: 'monospace' }}>{r.nrp}</td>
                  <td style={td}>{r.nama}</td>
                  <td style={td}>{r.pangkat_lama || '-'}</td>
                  <td style={td}>{r.pangkat_baru || '-'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{r.gaji_lama?.toLocaleString('id') || '-'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{r.gaji_baru?.toLocaleString('id') || '-'}</td>
                  <td style={{ ...td, fontSize: 12 }}>{r.tmt_gaji || '-'}</td>
                  <td style={td}><StatusBadge status={r.status} /></td>
                  <td style={td}>
                    <Btn size="sm" variant="danger" onClick={async () => {
                      if (!window.confirm(`Hapus penetapan gaji ${r.nrp}?`)) return;
                      try { await binkarAPI.deleteGaji(r.id); toast('Data gaji dihapus'); load(); } catch (e) { toast(e.message, 'error'); }
                    }}>Hapus</Btn>
                  </td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: '#9CA3AF' }}>Belum ada penetapan gaji</td></tr>}
            </tbody>
          </table>
        </div>
        <Pager page={page} total={total} limit={10} onChange={setPage} />
      </Card>
    </>
  );
};

// ── KEPUTUSAN ──────────────────────────────────────────
const KeputusanTab = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ jenis: '', no_kep: '', tgl_kep: '' });
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await binkarAPI.getKeputusan(`?page=${page}&limit=10`);
      if (r.success) { setData(r.data); setTotal(r.pagination?.total || 0); }
    } catch (err) { toast(err.message, 'error'); }
    finally { setLoading(false); }
  }, [page, toast]);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.jenis || !form.no_kep) { toast('Jenis dan nomor keputusan harus diisi', 'error'); return; }
    try {
      const r = await binkarAPI.createKeputusan(form);
      if (r.success) { toast('Keputusan berhasil disimpan'); setForm({ jenis: '', no_kep: '', tgl_kep: '' }); load(); }
    } catch (err) { toast(err.message, 'error'); }
  };

  return (
    <>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Buat Keputusan</h3>
        <div style={rowStyle}>
          <Field label="Jenis"><Select value={form.jenis} onChange={(e) => setForm({ ...form, jenis: e.target.value })}
            options={[{ value: '', label: '-- Pilih --' }, { value: 'pemindahan', label: 'Pemindahan' }, { value: 'penempatan', label: 'Penempatan' }, { value: 'kp', label: 'Kepangkatan' }]} /></Field>
          <Field label="Nomor Keputusan"><Input value={form.no_kep} onChange={(e) => setForm({ ...form, no_kep: e.target.value })} placeholder="KEP-2026-001" /></Field>
        </div>
        <div style={rowStyle}>
          <Field label="Tanggal Keputusan"><Input type="date" value={form.tgl_kep} onChange={(e) => setForm({ ...form, tgl_kep: e.target.value })} /></Field>
        </div>
        <Btn onClick={handleSubmit} disabled={loading}>{loading ? 'Menyimpan...' : 'Buat'}</Btn>
      </Card>
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Daftar Keputusan ({total})</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#F8FAFC' }}>
              <th style={th}>Jenis</th><th style={th}>Nomor Keputusan</th><th style={th}>Tanggal Keputusan</th><th style={th}>Status</th><th style={th}>Aksi</th>
            </tr></thead>
            <tbody>
              {data.map(r => (
                <tr key={r.id}>
                  <td style={td}>{r.jenis}</td>
                  <td style={td}>{r.no_kep}</td>
                  <td style={{ ...td, fontSize: 12 }}>{r.tgl_kep || '-'}</td>
                  <td style={td}><StatusBadge status={r.status} /></td>
                  <td style={td}>
                    {r.status === 'DRAFT' && <Btn size="sm" variant="danger" onClick={async () => {
                      if (!window.confirm('Hapus keputusan ini?')) return;
                      try { await binkarAPI.deleteKeputusan(r.id); toast('Keputusan dihapus'); load(); } catch (e) { toast(e.message, 'error'); }
                    }}>Hapus</Btn>}
                  </td>
                </tr>
              ))}
              {data.length === 0 && <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: '#9CA3AF' }}>Belum ada keputusan</td></tr>}
            </tbody>
          </table>
        </div>
        <Pager page={page} total={total} limit={10} onChange={setPage} />
      </Card>
    </>
  );
};

// ── MAIN ────────────────────────────────────────────────
export default function BinkarPage() {
  const [activeTab, setActiveTab] = useState('kp');
  const tabs = [
    { id: 'kp', label: 'Upload Kepangkatan' },
    { id: 'nominatif', label: 'Nominatif KP' },
    { id: 'pemindahan', label: 'Pemindahan' },
    { id: 'penempatan', label: 'Penempatan' },
    { id: 'gaji', label: 'Penetapan Gaji' },
    { id: 'keputusan', label: 'Keputusan' },
  ];

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0F172A', margin: 0 }}>BINKAR</h1>
        <p style={{ color: '#64748B', fontSize: 13, margin: '4px 0 0' }}>Binkar — Career Development & Promotion Management</p>
      </div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #E2E8F0', flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              color: activeTab === t.id ? '#2563EB' : '#64748B',
              borderBottom: activeTab === t.id ? '2px solid #2563EB' : '2px solid transparent', marginBottom: -2, whiteSpace: 'nowrap' }}>
            {t.label}
          </button>
        ))}
      </div>
      {activeTab === 'kp' && <UploadKPTab />}
      {activeTab === 'nominatif' && <NominatifKPTab />}
      {activeTab === 'pemindahan' && <PemindahanTab />}
      {activeTab === 'penempatan' && <PenempatanTab />}
      {activeTab === 'gaji' && <GajiTab />}
      {activeTab === 'keputusan' && <KeputusanTab />}
    </div>
  );
}
